import { lazy, Suspense, useEffect, useState } from 'react'

const PwaInstallDialog = lazy(() =>
  import('./PwaInstallDialog').then((module) => ({
    default: module.PwaInstallDialog,
  })),
)

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
}

const DISMISS_KEY = 'cardreplay:pwa-install-dismissed-at'
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1_000

function isStandalone() {
  const iosNavigator = navigator as Navigator & { standalone?: boolean }
  return (
    (window.matchMedia?.('(display-mode: standalone)').matches ?? false) ||
    iosNavigator.standalone === true
  )
}

function isIosMobile() {
  return /iPad|iPhone|iPod/i.test(navigator.userAgent)
}

function recentlyDismissed() {
  try {
    const value = Number(localStorage.getItem(DISMISS_KEY))
    return Number.isFinite(value) && Date.now() - value < DISMISS_TTL_MS
  } catch {
    return false
  }
}

function rememberDismissal() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
  } catch {
    // The offer can still be closed when storage is unavailable.
  }
}

export function PwaInstallPrompt({
  isGuideRoute = false,
}: {
  isGuideRoute?: boolean
}) {
  const [installEvent, setInstallEvent] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(recentlyDismissed)

  useEffect(() => {
    const captureInstallPrompt = (event: Event) => {
      event.preventDefault()
      if (!dismissed && !isStandalone()) {
        setInstallEvent(event as BeforeInstallPromptEvent)
      }
    }
    const installed = () => setInstallEvent(null)
    window.addEventListener('beforeinstallprompt', captureInstallPrompt)
    window.addEventListener('appinstalled', installed)
    return () => {
      window.removeEventListener('beforeinstallprompt', captureInstallPrompt)
      window.removeEventListener('appinstalled', installed)
    }
  }, [dismissed])

  const close = () => {
    rememberDismissal()
    setDismissed(true)
    setInstallEvent(null)
  }

  const install = async () => {
    if (!installEvent) return
    await installEvent.prompt()
    const choice = await installEvent.userChoice
    if (choice.outcome === 'dismissed') rememberDismissal()
    setInstallEvent(null)
    setDismissed(choice.outcome === 'dismissed')
  }

  const showIosGuide =
    !isGuideRoute && !dismissed && !isStandalone() && isIosMobile()
  const open = installEvent !== null || showIosGuide

  if (!open) return null

  return (
    <Suspense fallback={null}>
      <PwaInstallDialog
        canInstall={installEvent !== null}
        onClose={close}
        onInstall={install}
        showIosGuide={showIosGuide}
      />
    </Suspense>
  )
}
